import * as Sentry from '@sentry/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import {
    isRouteErrorResponse,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLocation,
} from 'react-router'
import { ThemeProvider } from 'styled-components'

import { ErrorMessage, lightTheme, TooltipProvider } from '@capture/capture-components'
import {
    trackClientStatsInternal,
    trackEvent,
    trackPageChange,
} from '@capture/client/analytics/eventTracking'
import { TestFlagService } from '@capture/client/API/services/TestFlagService'
import { _, cacheLocaleStrings } from '@capture/client/assets/localization/util'
import { getCurrentLocale } from '@capture/client/config/constants'
import { APIQueryProvider } from '@capture/client/query/APIQueryProvider'
import { SessionExpiredProvider } from '@capture/client/query/SessionExpiredProvider'

import type { Route } from './+types/root'
import { sessionMiddleware } from './middleware/sessionMiddleware'
import { getStore } from './store.client'

// The top most error boundary for the app, rendered when your app throws an error
// For more information, see https://reactrouter.com/start/framework/route-module#errorboundary
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    useEffect(() => {
        let details = 'An unexpected error occurred.'
        if (isRouteErrorResponse(error)) {
            details =
                error.status === 404
                    ? 'The requested page could not be found.'
                    : error.statusText || details
        } else if (import.meta.env.DEV && error && error instanceof Error) {
            details = error.message
        }

        Sentry.captureException(error, {
            level: 'error',
            tags: { captureScope: 'app_level' },
        })
        trackEvent('Error', 'AppErrorBoundaryInvoked', details)
    }, [error])

    return (
        <main id="error-page">
            <ErrorMessage>{_('something_went_wrong')}</ErrorMessage>
        </main>
    )
}

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en">
            <head>
                <meta charSet="UTF-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="icon" href="/assets/favicon.ico" sizes="48x48" />
                <link rel="apple-touch-icon" href="/assets/apple-touch-icon-180x180.png" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="robots" content="noindex" />
                <title>Min Sky</title>
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    )
}

// Shown while the client-side JavaScript is loading (SPA mode)
export function HydrateFallback() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100vw',
            }}>
            <div style={{ position: 'relative', width: 150, height: 150 }}>
                <svg viewBox="0 0 70 70" style={{ width: 150, height: 150 }} aria-hidden="true">
                    <g
                        stroke="none"
                        strokeWidth="1"
                        fill="#0091D2"
                        fillRule="evenodd"
                        transform="translate(0, 8)">
                        <path d="M67.5016835,7.33517849 C67.5016835,11.3559582 64.2406448,14.616494 60.2143128,14.616494 C56.1878665,14.616494 52.9248852,11.3559582 52.9248852,7.33517849 C52.9248852,3.31633875 56.1878665,0.0558029541 60.2143128,0.0558029541 C64.2406448,0.0558029541 67.5016835,3.31633875 67.5016835,7.33517849 L67.5016835,7.33517849 Z" />
                        <path d="M21.4590674,31.5210233 C22.0310091,28.4921439 23.3133925,25.7316665 25.1026956,23.379726 C23.2299725,20.3611171 19.9363657,18.3507273 16.1705785,18.3507273 C11.3808958,18.3507273 7.34633609,21.5909503 6.04361188,26.0284834 C2.64624426,26.4370204 0,29.3479033 0,32.9134724 C0,36.7635339 3.06151617,39.8797124 6.84364453,39.8797124 L11.1040098,39.8797124 C12.4313029,35.2897197 16.5227711,31.8584657 21.4590674,31.5210233 L21.4590674,31.5210233 Z" />
                        <path d="M59.0743149,30.3521282 C56.9146495,23.4101951 50.2746414,18.3507273 42.4131578,18.3507273 C33.0943373,18.3507273 25.5077972,25.4592705 25.0497868,34.3892266 C24.5326967,34.2977052 24.0074931,34.2448693 23.4660626,34.2448693 C18.3772105,34.2448693 14.2490603,38.2453363 14.2490603,43.1870359 C14.2490603,47.4395862 17.316633,51.3627963 21.4203285,52.2653435 L57.2015917,52.2653435 C63.4243853,52.2653435 69.038441,47.2363448 69.038441,41.201067 C69.038441,35.5906448 64.6824283,30.9802253 59.0743149,30.3521282 L59.0743149,30.3521282 Z" />
                    </g>
                </svg>
                <style>{`
                    @keyframes capture-ripple {
                        0% { transform: scale(1); opacity: 0.2; }
                        100% { transform: scale(4); opacity: 0; }
                    }
                    .capture-ripple-dot {
                        width: 30px;
                        height: 30px;
                        position: absolute;
                        top: 12%;
                        left: 76%;
                        background-color: #0091d2;
                        border-radius: 50%;
                        animation: capture-ripple 1.3s linear infinite;
                    }
                    .capture-ripple-dot-delayed {
                        animation-delay: 0.65s;
                    }
                `}</style>
                <div className="capture-ripple-dot" />
                <div className="capture-ripple-dot capture-ripple-dot-delayed" />
            </div>
        </div>
    )
}

export const clientMiddleware = [sessionMiddleware]

export async function clientLoader() {
    const locale = getCurrentLocale()
    cacheLocaleStrings(locale)

    // convert eventual params to test flags
    TestFlagService.paramsToFlags()

    return {
        store: getStore(),
    }
}

export default function Root({ loaderData }: Route.ComponentProps) {
    const { store } = loaderData

    const location = useLocation()

    useEffect(() => {
        trackPageChange(location.pathname, location.hash)
        trackClientStatsInternal(0, 60 * 60)
    }, [location])

    return (
        <APIQueryProvider>
            <SessionExpiredProvider>
                <ReduxProvider store={store}>
                    <ThemeProvider theme={lightTheme}>
                        <TooltipProvider>
                            <Outlet />
                        </TooltipProvider>
                    </ThemeProvider>
                </ReduxProvider>
            </SessionExpiredProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </APIQueryProvider>
    )
}
